import type {
  VolunteerOrgActivityDomain,
  VolunteerOrgProject,
  VolunteerOrgProjectAssignment,
  VolunteerOrganization,
} from "@entities";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  volunteerOrgActivityDomainRepo,
  volunteerOrgProjectAssignmentRepo,
  volunteerOrgProjectRepo,
} from "@repositories";
import {
  volunteerOrgActivationPhones,
  volunteerOrgHierarchy,
  volunteerOrgIsActivatable,
  volunteerOrgKindLabel,
} from "@shared/helpers/volunteer-organization";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useAlert } from "../../../contexts/AlertProvider";
import { VolunteerOrgLocationField } from "./VolunteerOrgSharedFields";
import {
  FormSection,
  primaryActionSx,
  quietButtonSx,
  selectedCardSx,
  surfaceCardSx,
} from "./volunteer-org-form-ui";
import { VolunteerOrgProjectAssignmentsMap } from "./VolunteerOrgProjectAssignmentsMap";

const emptyAssignmentDraft = () => ({
  volunteerOrganizationId: "",
  volunteerCount: "",
  address: "",
  coordinates: null as { lat: number; lng: number } | null,
  notes: "",
});

function assignmentOrgLabel(org?: VolunteerOrganization | null) {
  if (!org) return "ארגון לא נמצא";
  const hierarchy = volunteerOrgHierarchy(org);
  return hierarchy.parentName
    ? `${hierarchy.parentName} / ${hierarchy.title}`
    : hierarchy.title;
}

function assignmentGroups(rows: VolunteerOrgProjectAssignment[]) {
  const groups = new Map<
    string,
    {
      id: string;
      parentName: string;
      assignments: VolunteerOrgProjectAssignment[];
    }
  >();
  for (const row of rows) {
    const org = row.volunteerOrganization;
    const parentId = org?.parentId ?? org?.id ?? row.volunteerOrganizationId;
    const parentName =
      org?.parent?.name?.trim() ||
      (!org?.parentId ? org?.name?.trim() : "") ||
      "בלי ארגון אב";
    const existing = groups.get(parentId);
    if (existing) {
      existing.assignments.push(row);
    } else {
      groups.set(parentId, { id: parentId, parentName, assignments: [row] });
    }
  }
  return [...groups.values()].sort((a, b) =>
    a.parentName.localeCompare(b.parentName, "he"),
  );
}

export function VolunteerOrgProjectsTab({
  projects,
  organizations,
  onChange,
  onOpenOrganization,
}: {
  projects: VolunteerOrgProject[];
  organizations: VolunteerOrganization[];
  onChange: () => Promise<void>;
  onOpenOrganization?: (org: VolunteerOrganization) => void;
}) {
  const { setAlertText, setSeverity } = useAlert();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(
    projects[0]?.id ?? null,
  );
  const selected =
    projects.find((project) => project.id === selectedId) ?? projects[0] ?? null;
  const { data: allAssignments = [] } = useQuery({
    queryKey: ["volunteer-org-assignments"],
    queryFn: () =>
      volunteerOrgProjectAssignmentRepo.find({
        limit: 5000,
        orderBy: { createdAt: "desc" },
        include: {
          volunteerOrganization: {
            include: { parent: { include: { contacts: true } }, contacts: true },
          },
        },
      }),
  });
  const [draft, setDraft] = useState(emptyAssignmentDraft);

  const countsByProject = useMemo(() => {
    const map = new Map<string, { assignments: number; volunteers: number }>();
    for (const row of allAssignments) {
      const current = map.get(row.projectId) ?? {
        assignments: 0,
        volunteers: 0,
      };
      current.assignments += 1;
      current.volunteers += row.volunteerCount ?? 0;
      map.set(row.projectId, current);
    }
    return map;
  }, [allAssignments]);

  const selectedAssignments = useMemo(
    () =>
      allAssignments.filter((row) => row.projectId === selected?.id),
    [allAssignments, selected?.id],
  );
  const groupedAssignments = useMemo(
    () => assignmentGroups(selectedAssignments),
    [selectedAssignments],
  );
  const volunteerTotal = selectedAssignments.reduce(
    (sum, row) => sum + (row.volunteerCount ?? 0),
    0,
  );

  const orgOptions = useMemo(
    () =>
      organizations
        .filter(volunteerOrgIsActivatable)
        .sort((a, b) =>
          assignmentOrgLabel(a).localeCompare(assignmentOrgLabel(b), "he"),
        ),
    [organizations],
  );

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      gap={2}
      sx={{ p: { xs: 1.5, md: 2 }, pb: 3, flex: 1, minHeight: 0, overflow: "auto" }}
    >
      <Stack gap={1.5} sx={{ width: { md: 320 }, flexShrink: 0, minWidth: 0 }}>
        <FormSection title="פרויקט חדש">
          <Stack gap={1.5}>
            <TextField
              size="small"
              label="שם"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <TextField
              size="small"
              label="תיאור"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
            <Button
              variant="contained"
              disabled={!name.trim()}
              sx={{ ...primaryActionSx, alignSelf: "flex-start" }}
              onClick={async () => {
                try {
                  const created = await volunteerOrgProjectRepo.insert({
                    name: name.trim(),
                    description,
                    isActive: true,
                  });
                  setName("");
                  setDescription("");
                  setSelectedId(created.id);
                  await onChange();
                } catch (err) {
                  setSeverity("error");
                  setAlertText(err instanceof Error ? err.message : "יצירה נכשלה");
                }
              }}
            >
              יצירה
            </Button>
          </Stack>
        </FormSection>
        {projects.map((project) => {
          const counts = countsByProject.get(project.id);
          const isSelected = selected?.id === project.id;
          return (
            <Box
              key={project.id}
              sx={selectedCardSx(isSelected)}
              onClick={() => setSelectedId(project.id)}
            >
              <Stack gap={0.75}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={1}
                >
                  <Typography sx={{ fontWeight: 700 }}>{project.name}</Typography>
                  <Switch
                    size="small"
                    checked={project.isActive}
                    onClick={(event) => event.stopPropagation()}
                    onChange={async (_, checked) => {
                      await volunteerOrgProjectRepo.update(project.id, {
                        isActive: checked,
                      });
                      await onChange();
                    }}
                  />
                </Stack>
                {project.description ? (
                  <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                    {project.description}
                  </Typography>
                ) : null}
                <Stack direction="row" gap={0.75} flexWrap="wrap">
                  <Chip
                    size="small"
                    label={project.isActive ? "פעיל" : "כבוי"}
                    color={project.isActive ? "primary" : "default"}
                    variant={project.isActive ? "filled" : "outlined"}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${counts?.assignments ?? 0} שיוכים`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${counts?.volunteers ?? 0} מתנדבים`}
                  />
                </Stack>
              </Stack>
            </Box>
          );
        })}
      </Stack>
      <Stack gap={1.5} sx={{ flex: 1, minWidth: 0 }}>
        {selected ? (
          <>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              gap={2}
              alignItems="stretch"
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
            <FormSection
              title={`שיוכים לפרויקט ${selected.name}`}
              description={`${selectedAssignments.length} ארגונים ושלוחות · ${volunteerTotal} מתנדבים`}
            >
              {selectedAssignments.length === 0 ? (
                <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                  אין עדיין שיוכים לפרויקט זה.
                </Typography>
              ) : (
                <Stack gap={2}>
                  {groupedAssignments.map((group) => (
                    <Paper
                      key={group.id}
                      variant="outlined"
                      sx={{ ...surfaceCardSx, p: 0, overflow: "hidden" }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1.25,
                          bgcolor: "grey.50",
                          borderBottom: 1,
                          borderColor: "divider",
                        }}
                      >
                        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                          {group.parentName}
                        </Typography>
                        <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                          {group.assignments.length} שיוכים
                        </Typography>
                      </Box>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>ארגון / שלוחה</TableCell>
                            <TableCell>מתנדבים</TableCell>
                            <TableCell>מיקום השיוך</TableCell>
                            <TableCell>איש קשר</TableCell>
                            <TableCell width={48} />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {group.assignments.map((row) => {
                            const org = row.volunteerOrganization;
                            const hierarchy = org
                              ? volunteerOrgHierarchy(org)
                              : null;
                            const phones = org
                              ? volunteerOrgActivationPhones(org, org.parent)
                              : [];
                            const contact =
                              phones.find(
                                (phone) => phone.roleId === "volunteeringCoordinator",
                              ) ?? phones.find((phone) => phone.roleId === "head");
                            return (
                              <TableRow
                                key={row.id}
                                hover
                                sx={{
                                  cursor: org && onOpenOrganization ? "pointer" : "default",
                                }}
                                onClick={() => {
                                  if (org) onOpenOrganization?.(org);
                                }}
                              >
                                <TableCell>
                                  <Stack sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                                      {hierarchy?.title ?? assignmentOrgLabel(org)}
                                    </Typography>
                                    <Typography
                                      color="text.secondary"
                                      sx={{ fontSize: 12 }}
                                    >
                                      {org ? volunteerOrgKindLabel(org) : ""}
                                      {org?.address ? ` · ${org.address}` : ""}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell>{row.volunteerCount}</TableCell>
                                <TableCell>
                                  <Typography sx={{ fontSize: 13 }}>
                                    {row.address || ""}
                                  </Typography>
                                  {row.notes ? (
                                    <Typography
                                      color="text.secondary"
                                      sx={{ fontSize: 12 }}
                                    >
                                      {row.notes}
                                    </Typography>
                                  ) : null}
                                </TableCell>
                                <TableCell>
                                  <Typography sx={{ fontSize: 13 }}>
                                    {contact ? `${contact.label} · ${contact.name}` : ""}
                                  </Typography>
                                  <Typography
                                    color="text.secondary"
                                    sx={{ fontSize: 12 }}
                                  >
                                    {[contact?.phone, contact?.email]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    aria-label="מחיקת שיוך"
                                    onClick={async (event) => {
                                      event.stopPropagation();
                                      try {
                                        await volunteerOrgProjectAssignmentRepo.delete(
                                          row.id,
                                        );
                                        await onChange();
                                      } catch (err) {
                                        setSeverity("error");
                                        setAlertText(
                                          err instanceof Error
                                            ? err.message
                                            : "מחיקה נכשלה",
                                        );
                                      }
                                    }}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Paper>
                  ))}
                </Stack>
              )}
            </FormSection>
              </Box>
              {selectedAssignments.length > 0 ? (
                <Box sx={{ flex: 1.1, minWidth: 280 }}>
                  <VolunteerOrgProjectAssignmentsMap
                    key={selected.id}
                    projectName={selected.name}
                    assignments={selectedAssignments}
                    onOpenOrganization={onOpenOrganization}
                  />
                </Box>
              ) : null}
            </Stack>
            <FormSection title="שיוך ארגון לפרויקט">
              <Stack gap={1.5}>
                <TextField
                  select
                  size="small"
                  label="ארגון או שלוחה"
                  value={draft.volunteerOrganizationId}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      volunteerOrganizationId: event.target.value,
                    })
                  }
                >
                  {orgOptions.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {volunteerOrgKindLabel(org)} · {assignmentOrgLabel(org)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="מספר מתנדבים"
                  type="number"
                  value={draft.volunteerCount}
                  onChange={(event) =>
                    setDraft({ ...draft, volunteerCount: event.target.value })
                  }
                />
                <VolunteerOrgLocationField
                  required
                  label="מיקום השיוך"
                  value={{ address: draft.address, coordinates: draft.coordinates }}
                  onChange={(next) => setDraft({ ...draft, ...next })}
                />
                <TextField
                  size="small"
                  label="הערות"
                  value={draft.notes}
                  onChange={(event) =>
                    setDraft({ ...draft, notes: event.target.value })
                  }
                />
                <Button
                  variant="outlined"
                  disabled={!draft.volunteerOrganizationId || !draft.address.trim()}
                  sx={{ ...quietButtonSx, alignSelf: "flex-start" }}
                  onClick={async () => {
                    try {
                      await volunteerOrgProjectAssignmentRepo.insert({
                        projectId: selected.id,
                        volunteerOrganizationId: draft.volunteerOrganizationId,
                        volunteerCount: (() => {
                          const value = Number(draft.volunteerCount);
                          if (!Number.isFinite(value) || value < 0) {
                            throw new Error("מספר המתנדבים חייב להיות אפס או יותר");
                          }
                          return Math.floor(value);
                        })(),
                        address: draft.address,
                        coordinates: draft.coordinates,
                        notes: draft.notes.trim() || null,
                      });
                      setDraft(emptyAssignmentDraft());
                      await onChange();
                    } catch (err) {
                      setSeverity("error");
                      setAlertText(
                        err instanceof Error ? err.message : "שיוך נכשל",
                      );
                    }
                  }}
                >
                  שמירת שיוך
                </Button>
              </Stack>
            </FormSection>
          </>
        ) : (
          <Alert severity="info">בחרו פרויקט כדי לראות את כל הארגונים המשויכים.</Alert>
        )}
      </Stack>
    </Stack>
  );
}

export function VolunteerOrgDomainsTab({
  domains,
  onChange,
}: {
  domains: VolunteerOrgActivityDomain[];
  onChange: () => Promise<void>;
}) {
  const { setAlertText, setSeverity } = useAlert();
  const [name, setName] = useState("");
  return (
    <Stack gap={1.5} sx={{ p: { xs: 1.5, md: 2 }, pb: 3, maxWidth: 640 }}>
      <FormSection title="תחום חדש" description="סוג העשייה שמופיע בסינון ההפעלה.">
        <Stack direction="row" gap={1}>
          <TextField
            size="small"
            label="שם"
            value={name}
            onChange={(event) => setName(event.target.value)}
            fullWidth
          />
          <Button
            variant="contained"
            disabled={!name.trim()}
            sx={primaryActionSx}
            onClick={async () => {
              try {
                await volunteerOrgActivityDomainRepo.insert({
                  name: name.trim(),
                  isActive: true,
                  sortOrder: domains.length,
                });
                setName("");
                await onChange();
              } catch (err) {
                setSeverity("error");
                setAlertText(err instanceof Error ? err.message : "יצירה נכשלה");
              }
            }}
          >
            יצירה
          </Button>
        </Stack>
      </FormSection>
      {domains.map((domain) => (
        <Paper key={domain.id} variant="outlined" sx={{ ...surfaceCardSx, py: 1.25 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontWeight: 600 }}>{domain.name}</Typography>
            <Stack direction="row" alignItems="center" gap={1}>
              <Chip
                size="small"
                label={domain.isActive ? "פעיל" : "כבוי"}
                color={domain.isActive ? "primary" : "default"}
                variant={domain.isActive ? "filled" : "outlined"}
              />
              <Switch
                size="small"
                checked={domain.isActive}
                onChange={async (_, checked) => {
                  await volunteerOrgActivityDomainRepo.update(domain.id, {
                    isActive: checked,
                  });
                  await onChange();
                }}
              />
            </Stack>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}
